// Copyright (c) Ping. All rights reserved.
//
// Always-visible resize handle for the left side-panel column. Lives on the
// TabView wrapper (not inside the conversation panel) so resizing still works
// when the Activity scene is showing — the panel's own handle is suppressed via
// `hideResizeHandle` and this one owns the gesture for both scenes. Web-only
// (DOM mouse events); drives the shared `sidePanelWidth` preference.

import { useEffect, useRef, useState } from 'react';

import {
  SIDE_PANEL_MAX_WIDTH,
  SIDE_PANEL_MIN_WIDTH,
  useUiPreferencesStore,
} from '@ping/ui';

const HIT_WIDTH = 8;
const VISIBLE_WIDTH = 2;

export function SidePanelResizeHandle(): JSX.Element {
  const width = useUiPreferencesStore((s) => s.sidePanelWidth);
  const setWidth = useUiPreferencesStore((s) => s.setSidePanelWidth);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const onMove = (e: MouseEvent): void => {
      const drag = dragRef.current;
      if (drag === null) return;
      const next = Math.min(
        Math.max(drag.startWidth + (e.clientX - drag.startX), SIDE_PANEL_MIN_WIDTH),
        SIDE_PANEL_MAX_WIDTH,
      );
      setWidth(next);
    };
    const onUp = (): void => {
      if (dragRef.current === null) return;
      dragRef.current = null;
      setDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [setWidth]);

  const showIndicator = hovered || dragging;

  return (
    <div
      role="separator"
      aria-label="Resize conversations panel"
      aria-orientation="vertical"
      className="absolute top-0 bottom-0 z-20"
      style={{ cursor: 'col-resize', width: HIT_WIDTH, right: -(HIT_WIDTH / 2) }}
      onMouseDown={(e) => {
        e.preventDefault();
        dragRef.current = { startX: e.clientX, startWidth: width };
        setDragging(true);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`h-full mx-auto bg-light-accent-brand dark:bg-[#537EFF] ${
          showIndicator ? 'opacity-70' : 'opacity-0'
        }`}
        style={{ width: VISIBLE_WIDTH, pointerEvents: 'none' }}
      />
    </div>
  );
}
